import { Component, Input, OnInit, inject } from "@angular/core";
import { ResourceModel, WorkMode } from "@app/models/rest/resource";
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
  MatDialogTitle,
  MatDialogContent,
  MatDialogActions
} from "@angular/material/dialog";
import { ResourceStore } from "@app/services/store/resource-store";
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule
} from "@angular/forms";
import { Team } from "@app/models/rest/team";
import { Store } from "@ngrx/store";
import { LocationChooserComponent } from "@app/view/shared/location-chooser/location-chooser.component";
import { PostCodeMessage } from "@app/models/dto/postcode-message";
import { LocationModel } from "@app/models/rest/location";
import { LocationStore } from "@app/services/store/location-store";
import { TeamStore } from "@app/services/store/team-store";
import { Observable } from "rxjs";
import { CdkScrollable } from "@angular/cdk/scrolling";
import { MatIcon } from "@angular/material/icon";
import {
  MatFormField,
  MatLabel,
  MatError,
  MatSuffix
} from "@angular/material/form-field";
import { MatInput } from "@angular/material/input";
import { MatSelect } from "@angular/material/select";
import { MatOption } from "@angular/material/core";
import { MatIconButton, MatButton } from "@angular/material/button";
import { AsyncPipe } from "@angular/common";
import { DialogHeaderComponent } from "@app/view/shared/dialog-header/dialog-header.component";

@Component({
  selector: "app-resource-editor",
  templateUrl: "./resource-editor.component.html",
  styleUrls: ["./resource-editor.component.scss"],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    CdkScrollable,
    MatDialogContent,
    MatIcon,
    MatFormField,
    MatLabel,
    MatInput,
    MatError,
    MatSelect,
    MatOption,
    MatSuffix,
    MatIconButton,
    MatDialogActions,
    MatButton,
    AsyncPipe,
    DialogHeaderComponent
  ]
})
export class ResourceEditorComponent implements OnInit {
  resourceStore = inject(ResourceStore);
  locationStore = inject(LocationStore);
  teamStore = inject(TeamStore);
  protected store = inject<Store<any>>(Store);
  dialog = inject(MatDialog);
  dialogRef = inject<MatDialogRef<ResourceEditorComponent>>(MatDialogRef);
  data = inject(MAT_DIALOG_DATA);

  resourceForm = new UntypedFormGroup({
    resourceFC: new UntypedFormControl("", Validators.required),
    locationFC: new UntypedFormControl("", Validators.required),
    teamFC: new UntypedFormControl("", Validators.required)
  });

  @Input()
  resource: ResourceModel;
  teams: Team[] = [];
  selectedLocation: PostCodeMessage = null;
  teams$: Observable<Team[]>;

  constructor() {
    this.resource = new ResourceModel();
    this.resource.name = null;
    this.resource.workMode = WorkMode.Online; // Default to Online work mode
  }

  ngOnInit() {
    // Load the teams
    this.teamStore.loadAll(null).then(() => {
      console.log("Teams loaded");
    });

    // Get teams observable
    this.teams$ = this.teamStore.entities;
  }

  getErrorMessage(controlName: string) {
    console.log(this.resourceForm.controls[controlName].errors);
    console.log("get error");
    return this.resourceForm.controls[controlName].errors.server;
  }

  onCancelClick() {
    this.dialogRef.close();
  }

  onOkClick(): void {
    if (!this.resourceForm.valid) {
      return;
    }

    // Check if location is selected
    if (!this.selectedLocation) {
      alert("Please select a location");
      return;
    }

    // Check if team is selected
    const teamId = this.resourceForm.controls["teamFC"].value;
    if (!teamId) {
      alert("Please select a team");
      return;
    }

    const resource: ResourceModel = new ResourceModel();
    resource.name = this.resourceForm.controls["resourceFC"].value;
    resource.workMode = WorkMode.Online;

    // First create the resource
    this.resourceStore.create(resource).subscribe(
      (createdResource) => {
        // Get the team object
        this.teamStore.loadSingle(teamId).then((teamObject) => {
          // Assign team to resource
          createdResource.bindRelation("team", teamObject).subscribe(
            () => {
              console.log("Team assigned to resource");
              // After team is assigned, handle location
              this.assignLocationToResource(createdResource);
            },
            (error) => {
              console.error("Error assigning team:", error);
              // Still continue with location assignment
              this.assignLocationToResource(createdResource);
            }
          );
        });
      },
      (error) => {
        console.error("Error creating resource:", error);
        alert(error.error.message || "Error creating resource");
      }
    );
  }

  onNameChanged(event) {
    this.resource.name = event.target.value;
  }

  selectLocation() {
    this.dialog
      .open(LocationChooserComponent, {})
      .afterClosed()
      .subscribe((value: { result: PostCodeMessage }) => {
        if (!value || !value.result) return;

        console.log("Location selected:", value.result);
        this.selectedLocation = value.result;

        // Update form control to reflect selection
        this.resourceForm.controls["locationFC"].setValue(
          value.result.postCode || "Location selected"
        );
        this.resourceForm.controls["locationFC"].markAsDirty();
      });
  }

  private assignLocationToResource(resource: ResourceModel) {
    if (!this.selectedLocation) {
      this.dialogRef.close({ success: true });
      return;
    }

    const location = new LocationModel();
    location.latitude = this.selectedLocation.latitude;
    location.longitude = this.selectedLocation.longitude;

    // Check if a location already exists at these coordinates
    this.locationStore
      .findByLongitudeAndLatitude(location.latitude, location.longitude)
      .then((existing) => {
        if (existing.resources.length > 0) {
          // Use existing location
          console.log("Using existing location:", existing);
          resource.bindRelation("location", existing.resources[0]).subscribe(
            () => {
              console.log("Location assigned to resource");
              this.dialogRef.close({ success: true });
            },
            (error) => {
              console.error("Error binding location:", error);
              this.dialogRef.close({ success: true }); // Still close dialog as resource was created
            }
          );
        } else {
          // Create new location
          this.locationStore.create(location).subscribe(
            (createdLocation) => {
              resource
                .bindRelation("location", createdLocation as LocationModel)
                .subscribe(
                  () => {
                    console.log(
                      "New location created and assigned to resource"
                    );
                    this.dialogRef.close({ success: true });
                  },
                  (error) => {
                    console.error("Error binding new location:", error);
                    this.dialogRef.close({ success: true }); // Still close dialog as resource was created
                  }
                );
            },
            (error) => {
              console.error("Error creating location:", error);
              this.dialogRef.close({ success: true }); // Still close dialog as resource was created
            }
          );
        }
      })
      .catch((error) => {
        console.error("Error finding location:", error);
        this.dialogRef.close({ success: true }); // Still close dialog as resource was created
      });
  }
}
